import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DateModel } from '@app/core/components/date-range/date-range.component';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { GridExportArgGet, GridExportPriceListItem, P2ExportProcListItem, P2ExportProcService } from '@app/services/p2-export-proc.service';
import { BusinesscoItem, CoopItem, LogisticService, OrderStatusItem, OrderTypeItem, StockplaceItem } from '@app/services/logistic.service';
import { EconomyService } from '@app/services/economy.service';
import { CollectionKeynameItem, CustomerItem, LabourKeynameItem, OrderKeynameItem, SearchProjectItem, TypeaheadService } from '@app/services/typeahead.service';

@Component({
    selector: 'tt-grid-export-arg',
    templateUrl: './grid-export-arg.component.html',
    styleUrls: ['./grid-export-arg.component.css'],
    standalone: false,
})
export class GridExportArgComponent implements OnInit, OnDestroy {
    public page?: GridExportArgGet;
    public gridMain?: GridOptions;
    public gridDetail?: GridOptions;
    public gridThird?: GridOptions;
    public orderStatuses: OrderStatusItem[] = [];
    public orderTypes: OrderTypeItem[] = [];
    public companies: BusinesscoItem[] = [];
    public stockPlaces: StockplaceItem[] = [];
    public businessSites: Item[] = [];
    public coops: CoopItem[] = [];
    public priceLists: GridExportPriceListItem[] = [];
    public activeStatuses: P2ExportProcListItem[] = [];
    public executing = false;
    public error = '';
    public paneSizes = [50, 50];
    private searchTimer?: ReturnType<typeof setTimeout>;
    private selectedRow?: GridRow;
    private selectedDetail?: GridRow;
    private destroyed = false;
    private companyRevision = 0;
    private pendingRemember: Promise<void>[] = [];
    private stopResize?: () => void;

    constructor(
        private exportProc: P2ExportProcService,
        private logistic: LogisticService,
        private economy: EconomyService,
        private typeahead: TypeaheadService,
        private remember: RememberService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) { this.navigator = useNavigatorFor(navigator, this); }

    public get gridCount(): number { return Number(this.page?.grid_count ?? 1); }
    private get routeId(): string { return `${this.navigator?.params['argtype'] ?? ''}.${this.navigator?.params['argvalue'] ?? ''}`; }
    private get paneRememberId(): string { return `gridexportarg.${this.routeId}.${this.gridCount}boxes`; }

    public async ngOnInit(): Promise<void> {
        try {
            this.page = await this.exportProc.getGridExportArguments({ argtype: String(this.navigator?.params['argtype'] ?? ''), argvalue: String(this.navigator?.params['argvalue'] ?? ''), p2_exportproc_keyno: String(this.navigator?.params['p2_exportproc_keyno'] ?? '') });
            if (this.destroyed || !this.page) return;
            this.navigator?.setHeading?.(this.page.p2_exportproc_name ?? '', false);
            if (this.valid(this.page.p2_datatask_keyno) && this.valid(this.page.p2_exportproc_keyno)) {
                this.gridMain = this.createGrid('grid', this.page.p2_datatask_keyno!, () => ({ p2_exportproc_keyno: this.page!.p2_exportproc_keyno, ...this.selectedRow }), this.gridCount > 1)
                    .setRememberId(this.page.view_id || `w_gridexportarg.${this.routeId}.grid`)
                    .setOnSelect(event => { if (event.$item) void this.selectMainRow(event.$item); });
            }
            await Promise.all([this.loadLists(), this.loadPaneSizes()]);
        } catch (error) { this.showError(error); }
    }

    private createGrid(id: string, method: string | number, parameters: () => object, selectable: boolean): Grid {
        return new Grid().setRememberId(`w_gridexportarg.${this.routeId}.${id}`)
            .setLoadDataFunction({ method: Number(method), parameters, dynamic: true })
            .setKendoConfig({ height: '100%', aggregate: id === 'grid3' ? [] : true, pageable: false, filterable: id !== 'grid3', selectable: selectable ? 'single' : false })
            .setSpecialFunc({ newTab: id !== 'grid3' }).setCSSOptions({ altColor: true, textWrapping: false, height: 'fill' })
            .setToolbar({ pdfExport: true, excelExport: true, filter: true, columnVisibility: true, headers: true, edit: false, add: false, delete: false, save: false, wrapping: true, layouts: true });
    }

    public async selectMainRow(row: GridRow): Promise<void> {
        this.selectedRow = row;
        if (this.gridCount < 2 || !this.valid(this.page?.p2_datatask_keyno_2) || !this.valid(this.page?.p2_exportproc_keyno)) return;
        if (!this.gridDetail) this.gridDetail = this.createGrid('grid2', this.page!.p2_datatask_keyno_2!, () => this.selectedRow ?? {}, this.gridCount > 2)
            .setOnSelect(event => { if (event.$item) void this.selectDetailRow(event.$item); });
        else try { await this.gridDetail.gridfunc?.read(); } catch (error) { this.showError(error); }
    }

    public async selectDetailRow(row: GridRow): Promise<void> {
        this.selectedDetail = row;
        if (this.gridCount < 3 || !this.valid(this.page?.p2_datatask_keyno_3) || !this.valid(this.page?.p2_exportproc_keyno)) return;
        if (!this.gridThird) this.gridThird = this.createGrid('grid3', this.page!.p2_datatask_keyno_3!, () => this.selectedDetail ?? {}, false);
        else try { await this.gridThird.gridfunc?.read(); } catch (error) { this.showError(error); }
    }

    private async loadLists(): Promise<void> {
        const page = this.page!;
        await Promise.all([
            page.show_arg_orderstatus_id === '1' ? this.logistic.getOrderStatuses({ add_all: '1' }).then(items => this.orderStatuses = items) : undefined,
            page.show_arg_ordertype_id === '1' ? this.logistic.getOrderTypes({ add_all: '1' }).then(items => this.orderTypes = items) : undefined,
            page.show_arg_businessco_no === '1' ? this.logistic.getCompanies('1').then(items => this.companies = items) : undefined,
            page.show_arg_coop_id === '1' ? this.logistic.getCoops({ add_all: '1' }).then(items => this.coops = items) : undefined,
            page.show_arg_pricelist_id === '1' ? this.exportProc.getGridExportPriceLists().then(items => this.priceLists = items) : undefined,
            page.show_activestatus_id === '1' ? this.exportProc.getGridExportActiveStatuses().then(items => this.activeStatuses = items) : undefined,
            this.loadCompanyLists(),
        ]);
    }

    private async loadCompanyLists(): Promise<void> {
        const page = this.page;
        if (!page || !this.valid(page.businessco_no)) return;
        const revision = ++this.companyRevision;
        await Promise.all([
            page.show_arg_stockplace_id === '1' ? this.logistic.getStockplaces({ businessco_no: page.businessco_no, add_all: '1' }).then(items => { if (revision === this.companyRevision) this.stockPlaces = items; }) : undefined,
            page.show_arg_businesssite_keyno === '1' ? this.economy.getBusinessSites({ businessco_no: page.businessco_no, add_all: '1' }).then(items => { if (revision === this.companyRevision) this.businessSites = items; }) : undefined,
        ]);
    }

    // Backend grids read remembered arguments, so writes must finish before a refresh.
    private rememberField(id: FilterField): Promise<void> {
        const value = this.page?.[id];
        if (value === undefined || value === null) return Promise.resolve();
        const write = this.remember.remember(`w_gridexportarg.${id}_${this.page?.p2_exportproc_keyno}`, value);
        this.pendingRemember.push(write);
        void write.catch(error => this.showError(error));
        return write;
    }

    public async onChanged(value: string | undefined, id: FilterField): Promise<void> {
        if (!this.page || value === undefined || this.page[id] === value) return;
        this.page[id] = value;
        try {
            if (id === 'searchtext') {
                clearTimeout(this.searchTimer);
                if (value) { this.searchTimer = setTimeout(() => { void this.rememberField(id); }, 250); return; }
            } else if (!this.valid(value)) return;
            await this.rememberField(id);
            if (id === 'businessco_no') await this.loadCompanyLists();
            if (this.page.autoretrieve === '1' || id === 'businesssite_keyno') await this.executeGridExportArg();
        } catch (error) { this.showError(error); }
    }

    public async executeGridExportArg(): Promise<void> {
        if (this.executing || !this.gridMain?.gridfunc) return;
        this.executing = true;
        this.error = '';
        try {
            clearTimeout(this.searchTimer);
            if (this.page?.show_arg_searchtext === '1') this.rememberField('searchtext');
            await Promise.all(this.pendingRemember.splice(0));
            await this.gridMain.gridfunc.read();
        } catch (error) { this.showError(error); }
        finally { this.executing = false; }
    }

    public async onDateRangeChanged(dates: DateModel): Promise<void> {
        if (!this.page) return;
        const intervals = ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'];
        const index = intervals.indexOf(dates.dateselector_index);
        this.page.date_fom = dates.date_fom;
        this.page.date_tom = dates.date_tom;
        this.page.dateselector_index = index >= 0 ? String(index + 1) : Number(dates.dateselector_index) > 0 ? dates.dateselector_index : this.page.dateselector_index;
        try {
            await Promise.all((['date_fom', 'date_tom', 'dateselector_index'] as const).filter(id => !!this.page?.[id]).map(id => this.rememberField(id)));
            if (this.page.date_fom && this.page.date_tom && this.page.dateselector_index) {
                this.page.date_fomtom = this.page.date_fom + this.page.date_tom;
                if (this.page.autoretrieve === '1') await this.executeGridExportArg();
            }
        } catch (error) { this.showError(error); }
    }

    public searchOrders = (params?: { value?: string }) => this.typeahead.searchOrderKeynames(params?.value ?? this.page?.order_order_no_and_cust_name ?? '');
    public searchCollections = (params?: { value?: string }) => this.typeahead.searchCollectionKeynames(params?.value ?? this.page?.collection_name ?? '');
    public searchSuppliers = (params?: { value?: string }) => this.searchCustomers(params?.value ?? this.page?.supplier_name ?? '', 'S');
    private searchCustomers = (cust_name: string, custtype: 'S' | 'C') => this.typeahead.searchCustomer({ cust_name, filtertype: '', filtervalue: '', custtype, lastselect: 'SEARCH' });
    public searchCustomer = (params?: { value?: string }) => this.searchCustomers(params?.value ?? this.page?.cust_name ?? '', 'C');
    public searchProjects = (params?: { value?: string }) => this.typeahead.searchProjects({ project_name: params?.value ?? this.page?.project_name ?? '', businessco_no: this.page?.businessco_no || '0', cust_no: this.page?.cust_no || '0' });
    public searchLabours = (params?: { value?: string }) => this.typeahead.lookUpLabour({ labour_name: params?.value ?? this.page?.labour_name ?? '', businessco_no: this.page?.businessco_no || '0' });

    public async selectOrder(item?: OrderKeynameItem): Promise<void> {
        if (!this.page) return;
        this.page.order_internal_no = item?.order_id ?? '0';
        this.page.order_order_no_and_cust_name = item?.order_keyname ?? '';
        try {
            await this.rememberField('order_order_no_and_cust_name');
            if (item) await this.rememberField('order_internal_no');
            await this.executeGridExportArg();
        } catch (error) { this.showError(error); }
    }

    public async selectLookup(id: LookupField, item?: CustomerItem | SearchProjectItem | LabourKeynameItem | CollectionKeynameItem): Promise<void> {
        if (!this.page) return;
        let value = '0', label = '';
        if (item) {
            if ('collection_id' in item) { value = String(item.collection_id ?? '0'); label = String(item.collection_keyname ?? ''); }
            else if ('labour_no' in item) { value = String(item.labour_no ?? '0'); label = String(item.labour_name ?? ''); }
            else { value = String(item.item_id); label = item.item_name; }
        }
        this.page[id] = value;
        this.page[this.lookupName(id)] = label;
        try {
            if (id === 'collection_keyno') await this.rememberField('collection_name');
            await this.rememberField(id);
            await this.executeGridExportArg();
        } catch (error) { this.showError(error); }
    }

    public onLookupText(value: string, id: LookupField): void {
        if (!this.page || this.page[this.lookupName(id)] === value) return;
        this.page[this.lookupName(id)] = value;
        if (!value) void this.selectLookup(id);
    }
    public onOrderText(value: string): void {
        if (!this.page || this.page.order_order_no_and_cust_name === value) return;
        this.page.order_order_no_and_cust_name = value;
        if (!value) void this.selectOrder();
    }
    private lookupName(id: LookupField): LookupName {
        const names: Record<LookupField, LookupName> = { supplier_no: 'supplier_name', cust_no: 'cust_name', project_keyno: 'project_name', labour_no: 'labour_name', collection_keyno: 'collection_name' };
        return names[id];
    }

    private async loadPaneSizes(): Promise<void> {
        if (this.gridCount < 2) return;
        this.paneSizes = Array(this.gridCount).fill(100 / this.gridCount);
        const saved = await this.remember.getLastStatus(this.paneRememberId, true) as Record<string, number> | null;
        if (!saved) return;
        const sizes = ['grid', 'grid2', 'grid3'].slice(0, this.gridCount).map(id => Number(saved[id]));
        if (sizes.every(size => Number.isFinite(size) && size > 0)) this.paneSizes = sizes;
    }
    public startResize(event: PointerEvent, index: number, container: HTMLElement): void {
        event.preventDefault();
        this.stopResize?.();
        const top = event.clientY, sizes = [...this.paneSizes], total = sizes[index] + sizes[index + 1];
        const move = (next: PointerEvent) => {
            const offset = (next.clientY - top) / container.getBoundingClientRect().height * 100;
            const first = Math.max(5, Math.min(total - 5, sizes[index] + offset));
            this.paneSizes = sizes.map((size, i) => i === index ? first : i === index + 1 ? total - first : size);
            this.resizeGrids();
        };
        const stop = () => { document.removeEventListener('pointermove', move); document.removeEventListener('pointerup', finish); document.removeEventListener('pointercancel', stop); this.stopResize = undefined; };
        const finish = () => { stop(); void this.savePaneSizes(); };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', finish);
        document.addEventListener('pointercancel', stop);
        this.stopResize = stop;
    }
    public async resizePaneByKeyboard(event: KeyboardEvent, index: number): Promise<void> {
        if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const sizes = [...this.paneSizes], offset = event.key === 'ArrowUp' ? -2 : 2;
        if (sizes[index] + offset < 5 || sizes[index + 1] - offset < 5) return;
        sizes[index] += offset;
        sizes[index + 1] -= offset;
        this.paneSizes = sizes;
        this.resizeGrids();
        await this.savePaneSizes();
    }
    private async savePaneSizes(): Promise<void> {
        try { await this.remember.remember(this.paneRememberId, Object.fromEntries(this.paneSizes.map((size, index) => [['grid', 'grid2', 'grid3'][index], size]))); }
        catch (error) { this.showError(error); }
    }
    private resizeGrids(): void { [this.gridMain, this.gridDetail, this.gridThird].forEach(grid => grid?.gridfunc?.resize()); }
    private valid(value: unknown): boolean { return value !== undefined && value !== null && String(value).trim().length > 0; }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
    public ngOnDestroy(): void { this.destroyed = true; ++this.companyRevision; clearTimeout(this.searchTimer); this.stopResize?.(); }

}

type LookupField = 'supplier_no' | 'cust_no' | 'project_keyno' | 'labour_no' | 'collection_keyno';
type LookupName = 'supplier_name' | 'cust_name' | 'project_name' | 'labour_name' | 'collection_name';
type FilterField = LookupField | 'orderstatus_id' | 'ordertype_id' | 'businessco_no' | 'stockplace_id' | 'date_fom' | 'date_tom' | 'dateselector_index' | 'searchtext' | 'pricelist_id' | 'businesssite_keyno' | 'order_internal_no' | 'order_order_no_and_cust_name' | 'collection_name' | 'coop_id' | 'activestatus_id';
